/**
 * ReviewChatPanel — "ask about this run", the third column of /review.
 *
 * Triaging a long agent trajectory by eye does not work ("did it mark all
 * 108 shipments delivered?"). This panel puts a model in front of the same
 * trajectory the reviewer is scrolling, so the question can just be asked.
 *
 * The trajectory is NOT sent from here: `POST /api/review/chat` rebuilds it
 * from the trace key server-side. The panel only carries the questions.
 *
 * Mount with `key={item.trace_key}` — the thread is per queue item, restored
 * from sessionStorage on return and dropped when the tab closes.
 *
 * A fresh thread opens with the run-on-open question in the composer and,
 * unless the reviewer turned "run on open" off, asks it after a short wait
 * (TRA-1081): the first answer should be there before the trace is read,
 * and a row skimmed past with j/k should cost nothing.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from 'react'

import { Loader2, Send, Square, Trash2, X } from 'lucide-react'

import type { ReviewQueueItem } from '@/api/generated/model'
import { Markdown, type MarkdownComponents } from '@/components/review/Markdown'
import {
  linkRequestCitations,
  requestFromHref,
} from '@/components/review/requestCitations'
import { ReviewChatPromptPopover } from '@/components/review/ReviewChatPromptPopover'
import { REVIEW_QUESTION_CHIPS, RUN_ON_OPEN_QUESTION } from '@/components/review/reviewQuestions'
import {
  RUN_ON_OPEN_DELAY_MS,
  loadRunOnOpen,
  saveRunOnOpen,
  shouldRunOnOpen,
} from '@/components/review/runOnOpen'
import { useTeam } from '@/contexts/TeamContext'
import { useReviewChat } from '@/hooks/useReviewChat'
import type { Message } from '@/hooks/useStreamingChat'

interface SuggestionChipsProps {
  onPick: (question: string) => void
}

/**
 * The suggested questions, before the first reply AND under every later
 * one (TRA-1083). Reviewers read the first answer, then reach for these as
 * the next step, so they must not vanish once the thread has started.
 */
function SuggestionChips({ onPick }: SuggestionChipsProps): React.JSX.Element {
  return (
    <div className="flex flex-col items-start gap-1.5 pt-1">
      {REVIEW_QUESTION_CHIPS.map((suggestion) => (
        <button
          key={suggestion}
          type="button"
          onClick={() => {
            onPick(suggestion)
          }}
          className="border border-border px-2 py-1 text-left text-xs leading-relaxed text-muted-foreground hover:bg-muted"
        >
          {suggestion}
        </button>
      ))}
    </div>
  )
}

interface CitationProps {
  /** Requests in the trajectory on screen; citations beyond it stay text. */
  requestCount: number
  /** Scroll the transcript to request N (TRA-1080). */
  onRequestClick: (n: number) => void
}

/**
 * The `a` renderer for assistant bubbles: a `#request-N` href becomes an
 * in-page jump handled by the review page; any other link renders as is.
 */
function useCitationComponents(onRequestClick: (n: number) => void): MarkdownComponents {
  return useMemo(
    () => ({
      a: ({ href, children, node: _node, ...rest }) => {
        const n = requestFromHref(href)
        if (n === null) {
          return (
            <a href={href} {...rest}>
              {children}
            </a>
          )
        }
        return (
          <a
            href={href}
            title={`Scroll the transcript to request ${n.toString()}`}
            className="font-medium text-info"
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              // The hash must not land in the URL: `item` lives there, and
              // a stray fragment would be copied into every shared link.
              e.preventDefault()
              onRequestClick(n)
            }}
          >
            {children}
          </a>
        )
      },
    }),
    [onRequestClick],
  )
}

function MessageBubble({
  message,
  citations,
}: {
  message: Message
  citations: CitationProps
}): React.JSX.Element {
  const isUser = message.role === 'user'
  const components = useCitationComponents(citations.onRequestClick)
  // Linked while streaming too: the text is re-linked on every delta, which
  // is a cheap regex over a few KB.
  const text = isUser
    ? message.content
    : linkRequestCitations(message.content, citations.requestCount)
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[92%] px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? 'whitespace-pre-wrap bg-primary text-primary-foreground'
            : 'border border-border bg-card text-foreground'
        }`}
        role={isUser ? undefined : 'status'}
      >
        {message.content ? (
          isUser ? (
            message.content
          ) : (
            <Markdown text={text} components={components} />
          )
        ) : (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-muted-foreground"
            aria-label="Thinking"
          />
        )}
      </div>
    </div>
  )
}

interface ReviewChatPanelProps {
  item: ReviewQueueItem
  /** Close lives on the panel itself — the thing you want gone is the
   * thing that carries the control. */
  onClose: () => void
  /** How many gateway requests the transcript on screen has (TRA-1080). */
  requestCount: number
  /** Jump the transcript to request N when a reply's citation is clicked. */
  onRequestClick: (n: number) => void
}

export function ReviewChatPanel({
  item,
  onClose,
  requestCount,
  onRequestClick,
}: ReviewChatPanelProps): React.JSX.Element {
  const { messages, sendMessage, isStreaming, error, stop, clearMessages } =
    useReviewChat(item.trace_key, RUN_ON_OPEN_QUESTION)
  const { isRestricted, impersonatingTeamId, meLoaded } = useTeam()
  // The API refuses the POST from restricted demo teams and impersonating
  // staff; asking on their behalf would only paint an error on every row.
  // A failed /me leaves the restriction unknown, which is not a licence to
  // spend: the reviewer can still send the pre-filled question by hand.
  const readOnly = !meLoaded || isRestricted || impersonatingTeamId !== null
  const [runOnOpen, setRunOnOpen] = useState(loadRunOnOpen)
  // Decided once per mount, and the panel is keyed by trace, so once per
  // queue item: flipping the switch applies from the next item on. A row
  // that already has a verdict is opened to re-read, not to review, so it
  // is pre-filled but never auto-asked.
  const [autoAsk] = useState(() =>
    shouldRunOnOpen({
      enabled: runOnOpen,
      messageCount: messages.length,
      readOnly,
      reviewStatus: item.review_status,
    }),
  )
  // A fresh thread starts with the question ready to send, unless it is
  // about to be sent anyway.
  const [input, setInput] = useState(() =>
    messages.length === 0 && !autoAsk ? RUN_ON_OPEN_QUESTION : '',
  )

  // The question goes out after a short wait, not on mount: j/k past a row
  // unmounts this panel (it is keyed by trace) and the cleanup cancels the
  // timer, so skimming costs no turns. The ref keeps StrictMode's doubled
  // effect, and a `sendMessage` identity change, from asking twice; the
  // message-count check stands down if the reviewer picked a chip first.
  // The wait is visible: the empty state says the question is on its way.
  const autoAsked = useRef(false)
  const [pendingAutoAsk, setPendingAutoAsk] = useState(autoAsk)
  useEffect(() => {
    if (!autoAsk || autoAsked.current || messages.length > 0) return
    const timer = setTimeout(() => {
      autoAsked.current = true
      setPendingAutoAsk(false)
      void sendMessage(RUN_ON_OPEN_QUESTION)
    }, RUN_ON_OPEN_DELAY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [autoAsk, messages.length, sendMessage])

  // Pin the thread to the bottom as tokens stream in, and once more when
  // streaming ends: the suggestion chips mount at that moment, below the
  // last scroll position, and would otherwise sit just off-screen.
  const threadRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [messages, isStreaming])

  async function ask(text: string): Promise<void> {
    const question = text.trim()
    if (!question || isStreaming) return
    setInput('')
    await sendMessage(question)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    void ask(input)
  }

  function handleClear(): void {
    // Clear hands the question back to the reviewer; it does not re-spend.
    autoAsked.current = true
    setPendingAutoAsk(false)
    clearMessages()
    setInput(RUN_ON_OPEN_QUESTION)
  }

  function handleRunOnOpenChange(enabled: boolean): void {
    setRunOnOpen(enabled)
    saveRunOnOpen(enabled)
  }

  return (
    <aside
      aria-label="Ask about this run"
      className="flex w-[380px] flex-none flex-col border-l border-border bg-background"
    >
      <div className="flex items-center gap-1.5 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-lg leading-tight text-foreground">Ask about this run</h2>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Grounded in this trajectory
          </p>
        </div>
        <button
          type="button"
          onClick={handleClear}
          disabled={isStreaming || messages.length === 0}
          title="Clear this conversation"
          aria-label="Clear this conversation"
          className="flex h-6 w-6 flex-none items-center justify-center border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
        >
          <Trash2 aria-hidden="true" className="h-3 w-3" />
        </button>
        {/* The gear (TRA-1082): the team's instructions and run on open. */}
        <ReviewChatPromptPopover
          runOnOpen={runOnOpen}
          onRunOnOpenChange={handleRunOnOpenChange}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the ask panel"
          className="ml-1 flex h-6 w-6 flex-none items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <div ref={threadRef} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {pendingAutoAsk
                ? 'Asking "Review this run." in a moment. Move on and nothing is sent.'
                : 'Answers cite the request they came from, and say so when the ' +
                  'trajectory does not show something.'}
            </p>
            <SuggestionChips
              onPick={(question) => {
                void ask(question)
              }}
            />
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={index}
                message={message}
                citations={{ requestCount, onRequestClick }}
              />
            ))}
            {/* Under the latest reply, not pinned to the composer: the
                chips are the next step after reading, so they sit where
                the reading ends. Hidden while a reply streams so the
                thread does not jump around under the reader. */}
            {!isStreaming && (
              <SuggestionChips
                onPick={(question) => {
                  void ask(question)
                }}
              />
            )}
          </>
        )}
      </div>

      {error !== null && (
        <p role="alert" className="px-4 pb-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="border-t border-border p-3">
        <div className="flex flex-col gap-1.5 border border-border px-3 pb-2 pt-2.5 focus-within:border-muted-foreground">
          <label htmlFor="review-chat-input" className="sr-only">
            Ask about this run
          </label>
          <textarea
            id="review-chat-input"
            rows={2}
            placeholder="Ask a question…"
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
            }}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter newlines. The page's j/k queue nav
              // already ignores keystrokes inside a textarea.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void ask(input)
              }
            }}
            // TRA-1084: the reviewer drags the handle to fit a long question.
            // Native vertical resize with a floor and a ceiling; no splitter.
            className="max-h-[40vh] min-h-[3.25rem] w-full resize-y bg-transparent text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/60"
          />
          <div className="flex items-center gap-1.5">
            {isStreaming ? (
              <button
                type="button"
                onClick={stop}
                className="ml-auto flex h-6 items-center gap-1.5 border border-border px-3 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:bg-muted"
              >
                <Square className="h-3 w-3" />
                Stop
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim()}
                className="ml-auto flex h-6 items-center gap-1.5 bg-primary px-3 font-mono text-xs uppercase tracking-wider text-primary-foreground disabled:opacity-50"
              >
                <Send className="h-3 w-3" />
                Ask
              </button>
            )}
          </div>
        </div>
      </form>
    </aside>
  )
}
